/**
 * The proofs block of a pull request description: the Cofactor tests that
 * prove the change, or the reason none is possible. The server parses it from
 * the webhook payload; `cofactor proofs block` prints it, so an agent never
 * hand-formats one.
 *
 * ```
 * <!-- cofactor:proofs -->
 * - wf_3RHUz7Cm1xCt7HRqSAgNnW  filter bar keeps the Status filter after reload
 * <!-- /cofactor:proofs -->
 * ```
 *
 * or `none: <why no test is possible, and what would make one possible>`.
 *
 * @module proof-block
 */

const PROOFS_BLOCK_OPEN = '<!-- cofactor:proofs -->';
const PROOFS_BLOCK_CLOSE = '<!-- /cofactor:proofs -->';

/** One line of the block: a test and the run of it that proves the change (null when the line names none). */
export type ListedTest = { workflowId: string; runId: string | null };

/** What a description says: tests with their runs (ids as typed), or no test with a reason (possibly empty, which the check refuses). */
export type ProofBlock = { kind: 'none'; reason: string } | { kind: 'workflows'; tests: ListedTest[] };

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const LISTED = new RegExp(
  `^(?:[-*]|\\d+\\.)?\\s*(wf_[A-Za-z0-9]+|${UUID})(?![A-Za-z0-9])(?:\\s+(wrun_[A-Za-z0-9]+|${UUID})(?![A-Za-z0-9]))?`,
  'i'
);
const NONE = /^none:\s*/i;

/**
 * The first proofs block of `body`, or null when it has none (or never closes
 * it). A listed line is a test id, the id of the agent's run of it, then free
 * text for humans; lines that do not start with a test id are ignored.
 */
export function parseProofBlock(body: string | null | undefined): ProofBlock | null {
  if (!body) return null;
  const open = body.indexOf(PROOFS_BLOCK_OPEN);
  if (open < 0) return null;
  const start = open + PROOFS_BLOCK_OPEN.length;
  const close = body.indexOf(PROOFS_BLOCK_CLOSE, start);
  if (close < 0) return null;
  const inner = body.slice(start, close).trim();
  if (NONE.test(inner)) return { kind: 'none', reason: inner.replace(NONE, '').trim() };
  const tests = new Map<string, ListedTest>();
  for (const line of inner.split('\n')) {
    const match = LISTED.exec(line.replace(/`/g, '').trim());
    if (match && !tests.has(match[1]!)) tests.set(match[1]!, { workflowId: match[1]!, runId: match[2] ?? null });
  }
  return { kind: 'workflows', tests: [...tests.values()] };
}

/** The block for `parseProofBlock` to read back: one line per test (its id, its run, then what it proves), or the `none:` reason. */
export function formatProofBlock(
  block:
    | { kind: 'none'; reason: string }
    | { kind: 'workflows'; tests: { workflowId: string; runId: string; description: string }[] }
): string {
  const lines =
    block.kind === 'none'
      ? [`none: ${block.reason}`]
      : block.tests.map((test) => `- ${test.workflowId}  ${test.runId}  ${test.description}`);
  return [PROOFS_BLOCK_OPEN, ...lines, PROOFS_BLOCK_CLOSE].join('\n');
}

export type ProofRefusal = 'not-found' | 'approved-not-fork' | 'not-a-draft' | 'origin-unavailable';

/** A listed test as the server classified it: a new test, an edit of an approved one (a fork), or refused with why. */
export type ClassifiedProof =
  | { id: string; workflowId: string; kind: 'new'; name: string }
  | { id: string; workflowId: string; kind: 'fork'; name: string; originId: string; originName: string }
  | { id: string; refusal: ProofRefusal; message: string; name: string | null };

/** A listed test: what it is, and why the run listed for it is not proof (null when it is). */
export type EvaluatedProof = { test: ListedTest; proof: ClassifiedProof; runProblem: string | null };
